import React, { useState, useEffect, useRef } from 'react';
import './Login.css';
import { Icon } from './SharedComponents';

function Login({ onLogin }) {
    // Estados para o LOGIN
    const [email, setEmail] = useState('');
    const [senha, setSenha] = useState('');
    const [loginError, setLoginError] = useState('');
    const [emailError, setEmailError] = useState('');
    const [senhaError, setSenhaError] = useState('');
    const [genericAuthError, setGenericAuthError] = useState('');
    const [licencaError, setLicencaError] = useState('');
    const [userInactiveError, setUserInactiveError] = useState('');
    const [isLoadingLogin, setIsLoadingLogin] = useState(false);

    // Estados para o MODAL de "Esqueceu a senha?"
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [forgotEmail, setForgotEmail] = useState('');
    const [modalMessage, setModalMessage] = useState('');
    const [modalError, setModalError] = useState('');
    const [isLoadingForgot, setIsLoadingForgot] = useState(false);

    // Estados para o REGISTO
    const [nomeRegisto, setNomeRegisto] = useState('');
    const [emailRegisto, setEmailRegisto] = useState('');
    const [registoError, setRegistoError] = useState('');
    const [nomeError, setNomeError] = useState('');
    const [emailRegistoError, setEmailRegistoError] = useState('');
    const [registoSuccess, setRegistoSuccess] = useState('');
    const [isLoadingRegisto, setIsLoadingRegisto] = useState(false);

    const [isActive, setIsActive] = useState(false);

    // ======================================================
    // ✅ ESTADO DE CONEXÃO COM O SERVIDOR
    // ======================================================
    const [serverOnline, setServerOnline] = useState(true);

    // Flag para evitar chamadas duplicadas
    const isLoggingIn = useRef(false);

    // Estado para o vídeo de fundo
    const [videoSource, setVideoSource] = useState(getVideoByTime());
    const videoRef = useRef(null);

    // Função para determinar qual vídeo usar baseado na hora
    function getVideoByTime() {
        const now = new Date();
        const hours = now.getHours();

        if (hours >= 6 && hours < 18) {
            return '/videos/dia.mp4';
        } else {
            return '/videos/noite.mp4';
        }
    }

    // Função para aplicar dark mode diretamente
    const updateDarkMode = () => {
        const now = new Date();
        const hours = now.getHours();
        const isNight = hours >= 18 || hours < 6;

        console.log('Hora atual:', hours, 'É noite?', isNight);

        if (isNight) {
            document.body.classList.add('dark-mode');
            console.log('✅ Dark mode ATIVADO');
        } else {
            document.body.classList.remove('dark-mode');
            console.log('❌ Dark mode DESATIVADO');
        }
    };

    // Função para verificar e trocar o vídeo
    const checkAndUpdateVideo = () => {
        const newSource = getVideoByTime();
        setVideoSource(prevSource => {
            if (prevSource !== newSource) {
                console.log('Trocando vídeo para:', newSource);
                updateDarkMode();
                return newSource;
            }
            return prevSource;
        });
    };

    // Configurar o vídeo inicial e verificar mudanças
    useEffect(() => {
        updateDarkMode();

        const intervalId = setInterval(() => {
            checkAndUpdateVideo();
            updateDarkMode();
        }, 10000);

        const handleFocus = () => {
            checkAndUpdateVideo();
            updateDarkMode();
        };

        window.addEventListener('focus', handleFocus);

        return () => {
            clearInterval(intervalId);
            window.removeEventListener('focus', handleFocus);
        };
    }, []);

    useEffect(() => {
        document.body.classList.add('login-page');
        return () => {
            document.body.classList.remove('login-page');
            document.body.classList.remove('dark-mode');
        };
    }, []);

    // ======================================================
    // ✅ VERIFICAÇÃO DE CONEXÃO COM O SERVIDOR
    // Usa a mesma rota do Dashboard: /api/auth/check-server
    // Deteta: servidor offline, browser offline, aba escondida
    // ======================================================
    useEffect(() => {
        let isMounted = true;
        let intervalId;

        const checkServer = async () => {
            // ✅ 1. Se o browser está offline, não tenta fetch
            if (!navigator.onLine) {
                if (isMounted) setServerOnline(false);
                return;
            }

            try {
                const controller = new AbortController();
                const timeoutId = setTimeout(() => controller.abort(), 5000);

                // ✅ 2. Usa a mesma rota do Dashboard
                const token = localStorage.getItem('token');
                const res = await fetch('/api/auth/check-server', {
                    method: 'GET',
                    signal: controller.signal,
                    cache: 'no-store',
                    headers: {
                        'Cache-Control': 'no-cache',
                        ...(token && { 'Authorization': `Bearer ${token}` })
                    }
                });

                clearTimeout(timeoutId);

                if (isMounted) {
                    setServerOnline(res.ok);
                }
            } catch (err) {
                if (isMounted) {
                    setServerOnline(false);
                    console.warn('⚠️ Servidor offline ou inacessível:', err.message);
                }
            }
        };

        // Verificação inicial
        checkServer();

        // ✅ 3. Verifica a cada 10 segundos (igual ao Dashboard)
        intervalId = setInterval(checkServer, 10000);

        // ✅ 4. Verifica quando a aba volta a ficar visível
        const handleVisibilityChange = () => {
            if (!document.hidden) checkServer();
        };
        document.addEventListener('visibilitychange', handleVisibilityChange);

        // ✅ 5. Deteção INSTANTÂNEA do browser offline/online
        const handleOnline = () => {
            console.log('🌐 Browser online — verificando servidor...');
            checkServer();
        };
        const handleOffline = () => {
            console.log('📵 Browser offline — servidor marcado como offline');
            if (isMounted) setServerOnline(false);
        };
        window.addEventListener('online', handleOnline);
        window.addEventListener('offline', handleOffline);

        return () => {
            isMounted = false;
            clearInterval(intervalId);
            document.removeEventListener('visibilitychange', handleVisibilityChange);
            window.removeEventListener('online', handleOnline);
            window.removeEventListener('offline', handleOffline);
        };
    }, []);

    const validateEmail = (email) => {
        const regex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
        return regex.test(email);
    };

    const validateSenha = (senha) => {
        return senha.length >= 6;
    };

    const validateNome = (nome) => {
        return nome.trim().length >= 3;
    };

    const clearAllLoginErrors = () => {
        setEmailError('');
        setSenhaError('');
        setGenericAuthError('');
        setLoginError('');
        setLicencaError('');
        setUserInactiveError('');
    };

    // ======================================================
    // FUNÇÃO PARA ABRIR O MODAL
    // ======================================================
    const openModal = (e) => {
        e.preventDefault();
        setForgotEmail('');
        setModalMessage('');
        setModalError('');
        setIsModalOpen(true);
    };

    // ======================================================
    // FUNÇÃO PARA FECHAR O MODAL
    // ======================================================
    const closeModal = () => {
        setIsModalOpen(false);
        setForgotEmail('');
        setModalMessage('');
        setModalError('');
        setIsLoadingForgot(false);
    };

    // ======================================================
    // FUNÇÃO PARA ENVIAR PEDIDO DE RESET DE SENHA
    // ======================================================
    const handleForgotPasswordSubmit = async () => {
        setModalMessage('');
        setModalError('');

        if (!forgotEmail.trim()) {
            setModalError('Por favor, insira o seu email');
            return;
        }

        if (!validateEmail(forgotEmail)) {
            setModalError('Por favor, insira um email válido');
            return;
        }

        setIsLoadingForgot(true);

        try {
            const res = await fetch('/api/auth/forgot-password', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ email: forgotEmail })
            });

            const data = await res.json();

            if (res.ok) {
                setModalMessage(data.message || 'Pedido de recuperação de senha enviado! Entraremos em contacto consigo em breve.');
                setTimeout(() => {
                    closeModal();
                }, 3000);
            } else {
                setModalError(data.message || 'Erro ao solicitar recuperação de senha. Tente novamente.');
            }
        } catch (err) {
            setModalError('Erro ao conectar ao servidor. Tente novamente mais tarde.');
        } finally {
            setIsLoadingForgot(false);
        }
    };

    // ======================================================
    // HANDLE LOGIN - CORRIGIDO COM VERIFICAÇÃO DE USER ATIVO
    // ======================================================
    const handleSubmit = async (e) => {
        e.preventDefault();

        if (isLoggingIn.current) {
            console.log('⚠️ Login já em andamento, ignorando...');
            return;
        }

        clearAllLoginErrors();

        let hasError = false;

        if (!email.trim()) {
            setEmailError('Por favor, insira o seu email');
            hasError = true;
        } else if (!validateEmail(email)) {
            setEmailError('Introduza uma parte após "@". "' + email + '" está incompleto.');
            hasError = true;
        }

        if (!senha) {
            setSenhaError('Por favor, insira a sua senha');
            hasError = true;
        } else if (!validateSenha(senha)) {
            setSenhaError('A senha deve ter no mínimo 6 caracteres');
            hasError = true;
        }

        if (hasError) return;

        isLoggingIn.current = true;
        setIsLoadingLogin(true);

        try {
            const res = await fetch('/api/auth/login', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ email, senha })
            });

            const data = await res.json();

            if (res.ok) {
                if (data.usuario && data.usuario.ativo === false) {
                    console.log('❌ Utilizador desativado!');
                    setUserInactiveError('⚠️ Esta conta está desativada. Contacte o administrador para reativá-la.');
                    setIsLoadingLogin(false);
                    isLoggingIn.current = false;
                    return;
                }

                localStorage.setItem('token', data.token);

                if (data.usuario) {
                    localStorage.setItem('usuario', JSON.stringify(data.usuario));
                }

                if (data.licenca) {
                    localStorage.setItem('licenca', JSON.stringify(data.licenca));
                    console.log('✅ Licença salva:', data.licenca);
                } else {
                    console.warn('⚠️ Nenhuma licença retornada no login');
                }

                console.log('✅ Login bem-sucedido!');
                console.log('📊 Usuário ativo:', data.usuario?.ativo);

                window.location.href = '/dashboard';

            } else {
                const errorMsg = data.erro || data.message || '';
                const errorMsgLower = errorMsg.toLowerCase();

                if (errorMsgLower.includes('desativado') ||
                    errorMsgLower.includes('inativo') ||
                    errorMsgLower.includes('usuário desativado') ||
                    errorMsgLower.includes('user desativado')) {
                    setUserInactiveError('⚠️ Esta conta está desativada. Contacte o administrador para reativá-la.');
                    setEmailError('');
                    setSenhaError('');
                    setGenericAuthError('');
                    setLicencaError('');
                }
                else if (errorMsgLower.includes('licença') ||
                    errorMsgLower.includes('licenca') ||
                    errorMsgLower.includes('expirada') ||
                    errorMsgLower.includes('desativada') ||
                    errorMsgLower.includes('nenhuma licença')) {

                    setLicencaError(errorMsg);
                    setEmailError('');
                    setSenhaError('');
                    setGenericAuthError('');
                    setUserInactiveError('');
                }
                else if (errorMsgLower.includes('email') || errorMsgLower.includes('usuário') || errorMsgLower.includes('user') || errorMsgLower.includes('encontrado')) {
                    setEmailError(errorMsg);
                    setUserInactiveError('');
                }
                else if (errorMsgLower.includes('senha') || errorMsgLower.includes('password')) {
                    setSenhaError(errorMsg);
                    setUserInactiveError('');
                }
                else {
                    setGenericAuthError('Email ou senha incorretos. Tente novamente.');
                    setEmailError(' ');
                    setSenhaError(' ');
                    setUserInactiveError('');
                }
            }
        } catch (err) {
            console.error('Erro no login:', err);
            setLoginError('Erro ao conectar ao servidor. Verifique sua conexão com a internet.');
        } finally {
            setIsLoadingLogin(false);
            isLoggingIn.current = false;
        }
    };

    // ======================================================
    // HANDLE REGISTO COM LOADING
    // ======================================================
    const handleRegisto = async (e) => {
        e.preventDefault();

        setRegistoError('');
        setNomeError('');
        setEmailRegistoError('');
        setRegistoSuccess('');

        setIsLoadingRegisto(true);

        let hasError = false;

        if (!validateNome(nomeRegisto)) {
            setNomeError('O nome deve ter no mínimo 4 caracteres');
            hasError = true;
        }

        if (!validateEmail(emailRegisto)) {
            setEmailRegistoError('Por favor, insira um email válido');
            hasError = true;
        }

        if (hasError) {
            setIsLoadingRegisto(false);
            return;
        }

        try {
            const res = await fetch('/api/auth/register-interest', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                    nome: nomeRegisto,
                    email: emailRegisto
                })
            });

            const data = await res.json();

            if (res.ok) {
                setRegistoSuccess('Obrigado pelo interesse! Entraremos em contacto consigo com os requisitos e planos de pagamento.');
                setNomeRegisto('');
                setEmailRegisto('');

                setTimeout(() => {
                    setRegistoSuccess('');
                }, 5000);
            } else {
                const errorMsg = data.message || 'Erro ao registar interesse';
                setRegistoError(errorMsg);

                if (errorMsg.toLowerCase().includes('email')) {
                    setEmailRegistoError('Este email já está registado');
                }
            }
        } catch (err) {
            setRegistoError('Erro ao conectar ao servidor. Tente novamente mais tarde.');
        } finally {
            setIsLoadingRegisto(false);
        }
    };

    const handleEmailChange = (e) => {
        setEmail(e.target.value);
        clearAllLoginErrors();
    };

    const handleSenhaChange = (e) => {
        setSenha(e.target.value);
        clearAllLoginErrors();
    };

    // Ano atual para o copyright do footer
    const currentYear = new Date().getFullYear();

    return (
        <>
            {/* ============================================================
                VÍDEO DE FUNDO
               ============================================================ */}
            <div className="video-background">
                <video
                    ref={videoRef}
                    key={videoSource}
                    autoPlay
                    loop
                    muted
                    playsInline
                    style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                >
                    <source src={videoSource} type="video/mp4" />
                </video>
            </div>
            <div className="video-overlay"></div>

            {/* ============================================================
                CONTAINER PRINCIPAL (CARD DE LOGIN/REGISTO)
               ============================================================ */}
            <div className="container-background">
                <div className={`container ${isActive ? 'active' : ''}`}>

                    {/* ---------- FORMULÁRIO DE LOGIN ---------- */}
                    <div className="form-container sign-in">
                        <form onSubmit={handleSubmit}>
                            <h1>Membro</h1>
                            <p>use seu email e senha</p>

                            {loginError && (
                                <div className="global-error">
                                    <p>{loginError}</p>
                                </div>
                            )}

                            {userInactiveError && (
                                <div className="user-inactive-error">
                                    <div className="user-inactive-header">
                                        <div className="user-inactive-icon">🚫</div>
                                        <div className="user-inactive-title">Conta Desativada</div>
                                    </div>
                                    <div className="user-inactive-message">{userInactiveError}</div>
                                    <div className="user-inactive-help">
                                        <small>Contacte o administrador do sistema para reativar a sua conta.</small>
                                    </div>
                                </div>
                            )}

                            {licencaError && (
                                <div className="licenca-error">
                                    <div className="licenca-error-header">
                                        <div className="licenca-error-icon">⚠️</div>
                                        <div className="licenca-error-title">Erro de Licença</div>
                                    </div>
                                    <div className="licenca-error-message">{licencaError}</div>
                                </div>
                            )}

                            <div className="input-group">
                                <input
                                    type="email"
                                    placeholder="Email"
                                    value={email}
                                    onChange={handleEmailChange}
                                    className={emailError ? 'input-error' : ''}
                                />
                                {emailError && emailError !== ' ' && (
                                    <div className="error-message-field">{emailError}</div>
                                )}
                            </div>

                            <div className="input-group">
                                <input
                                    type="password"
                                    placeholder="Senha"
                                    value={senha}
                                    onChange={handleSenhaChange}
                                    className={senhaError ? 'input-error' : ''}
                                />
                                {senhaError && senhaError !== ' ' && (
                                    <div className="error-message-field">{senhaError}</div>
                                )}
                            </div>

                            {genericAuthError && (
                                <div className="generic-auth-error">
                                    <p>{genericAuthError}</p>
                                </div>
                            )}

                            <a href="#" onClick={openModal}>
                                Esqueceu a senha?
                            </a>

                            <button type="submit" disabled={isLoadingLogin}>
                                {isLoadingLogin ? 'A entrar...' : 'ENTRAR'}
                            </button>
                        </form>
                    </div>

                    {/* ---------- FORMULÁRIO DE REGISTO ---------- */}
                    <div className="form-container sign-up">
                        <form onSubmit={handleRegisto}>
                            <h1>Criar Conta</h1>
                            <p>use seu nome e email</p>

                            {registoSuccess && (
                                <div className="success-message-box">
                                    <p>{registoSuccess}</p>
                                </div>
                            )}

                            {registoError && (
                                <div className="global-error">
                                    <p>{registoError}</p>
                                </div>
                            )}

                            <div className="input-group">
                                <input
                                    type="text"
                                    placeholder="Nome"
                                    value={nomeRegisto}
                                    onChange={(e) => {
                                        setNomeRegisto(e.target.value);
                                        setNomeError('');
                                        setRegistoError('');
                                    }}
                                    className={nomeError ? 'input-error' : ''}
                                />
                                {nomeError && <div className="error-message-field">{nomeError}</div>}
                            </div>

                            <div className="input-group">
                                <input
                                    type="email"
                                    placeholder="Email"
                                    value={emailRegisto}
                                    onChange={(e) => {
                                        setEmailRegisto(e.target.value);
                                        setEmailRegistoError('');
                                        setRegistoError('');
                                    }}
                                    className={emailRegistoError ? 'input-error' : ''}
                                />
                                {emailRegistoError && <div className="error-message-field">{emailRegistoError}</div>}
                            </div>

                            <p align="center">Entraremos em contacto consigo com os requisitos e planos de pagamento</p>

                            <button type="submit" disabled={isLoadingRegisto}>
                                {isLoadingRegisto ? 'A enviar...' : 'SOLICITAR ACESSO'}
                            </button>
                        </form>
                    </div>

                    {/* ---------- TOGGLE (PAINEL ROXO) ---------- */}
                    <div className="toggle-container">
                        <div className="toggle">
                            <div className="toggle-panel toggle-left">
                                <h2>Se já é membro</h2>
                                <p>Então é só entrar</p>
                                <button className="hidden" onClick={() => setIsActive(false)}>
                                    Login
                                </button>
                            </div>
                            <div className="toggle-panel toggle-right">
                                <h2>Quer fazer parte da família?</h2>
                                <p>Registe-se e junte-se a nós</p>
                                <button className="hidden" onClick={() => setIsActive(true)}>
                                    Registar
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            {/* ============================================================
                LOADING OVERLAY PARA LOGIN
               ============================================================ */}
            {isLoadingLogin && (
                <div className="loading-overlay">
                    <div className="loading-container">
                        <div className="loading-spinner"></div>
                        <p>Autenticando...</p>
                    </div>
                </div>
            )}

            {/* ============================================================
                MODAL - POPUP PARA ESQUECEU A SENHA
               ============================================================ */}
            {isModalOpen && (
                <div className="modal-overlay" onClick={closeModal}>
                    <div className="modal-container" onClick={(e) => e.stopPropagation()}>
                        <div className="modal-header-senha">
                            <h3>🔐 Recuperar Senha</h3>
                            <button className="modal-close" onClick={closeModal}>×</button>
                        </div>
                        <div className="modal-body-senha">
                            <p>Insira o seu email registado no sistema para solicitar a recuperação da senha.</p>
                            <input
                                type="email"
                                placeholder="Seu email"
                                value={forgotEmail}
                                onChange={(e) => setForgotEmail(e.target.value)}
                                autoFocus
                            />
                            {modalMessage && (
                                <div className="modal-message success">{modalMessage}</div>
                            )}
                            {modalError && (
                                <div className="modal-message error">{modalError}</div>
                            )}
                        </div>
                        <div className="modal-footer-senha">
                            <button className="btn-cancel" onClick={closeModal}>Cancelar</button>
                            <button
                                className="btn-submit"
                                onClick={handleForgotPasswordSubmit}
                                disabled={isLoadingForgot}
                            >
                                {isLoadingForgot ? 'A enviar...' : 'Enviar Pedido'}
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {/* ============================================================
                FOOTER PROFISSIONAL
               ============================================================ */}
            <footer className="login-footer">
                {/* Links de navegação */}
                <nav className="login-footer__links" aria-label="Links do rodapé">
                    <a href="https://raf-erp.com" target="_blank" rel="noopener noreferrer">
                        <Icon name="website" size={14} />
                        <span>Website</span>
                    </a>
                    <span className="login-footer__sep" aria-hidden="true">•</span>
                    <a href="https://raf-erp.com/sobre" target="_blank" rel="noopener noreferrer">
                        <Icon name="info" size={14} />
                        <span>Sobre</span>
                    </a>
                    <span className="login-footer__sep" aria-hidden="true">•</span>
                    <a href="https://raf-erp.com/privacidade" target="_blank" rel="noopener noreferrer">
                        <Icon name="lock" size={14} />
                        <span>Privacidade</span>
                    </a>
                    <span className="login-footer__sep" aria-hidden="true">•</span>
                    <a href="mailto:suporte@raf-erp.com">
                        <Icon name="email" size={14} />
                        <span>Suporte</span>
                    </a>
                </nav>

                {/* Copyright */}
                <p className="login-footer__copyright">
                    © {currentYear} <strong>RAF ERP</strong>
                    <span className="login-footer__sep" aria-hidden="true">•</span>
                    <span>
                        Desenvolvido com
                        <Icon name="favorite" size={12} color="#ef4444" />
                        em Moçambique
                    </span>
                </p>

                {/* ✅ Versão + Status (verde/vermelho dinâmico) */}
                <div
                    className={`login-footer__version ${serverOnline ? 'is-online' : 'is-offline'}`}
                    title={serverOnline ? 'Sistema operacional' : 'Servidor offline'}
                >
                    <span className="login-footer__version-dot" aria-hidden="true"></span>
                    <span>v1.0.0</span>
                </div>
            </footer>
        </>
    );
}

export default Login;